import React from 'react';
import { useQuery } from 'react-query';
import { useRouting } from 'hooks/useRouting';
import { useTargetSupportRouting } from 'components/features/target-achievement-support/hooks/useTargetSupportRouting';
import { TargetItemIcon } from 'components/features/target-achievement-support/components/features/index/TargetItemIcon';
import { TargetType } from 'components/features/target-achievement-support/utils/type';
import {
  isIosAppNotPremium,
  isPremium,
} from 'components/features/utils/userStatus';
import Loading from 'components/commons/loading/Loading';
import { getPeriodGoal } from 'config/api';
import { AssetsList } from './totalAsset/AssetsList';

export type FluctuationIconType = 'day' | 'week' | 'month';

export const ServiceList = ({
  isTransition = true,
}: {
  isTransition?: boolean;
}) => {
  const { pushRouting } = useRouting();
  const srviceList = [
    {
      name: '口座一覧',
      icon: 'securities',
      link: 'securityaccounts',
      isExternalLink: false,
    },
    {
      name: (
        <>
          ペルソナ
          <br />
          チャート
        </>
      ),
      icon: 'human_pin',
      link: 'personachart',
      isExternalLink: false,
    },
    {
      name: '資産推移',
      icon: 'area-graph2',
      link: 'assettransition',
      isExternalLink: false,
    },
    {
      name: (
        <>
          資産
          <br />
          グループ
        </>
      ),
      icon: 'folder',
      link: 'grouping',
      isExternalLink: false,
    },
    {
      name: '適時開示',
      icon: 'disclosure',
      link: 'notificationList/4/1/1',
      isExternalLink: false,
    },
    {
      name: 'お知らせ',
      icon: 'info',
      link: 'notificationList/1/1/1',
      isExternalLink: false,
    },
    {
      name: (
        <>
          お知らせ
          <br />
          設定
        </>
      ),
      icon: 'settings',
      link: 'notificationSetting',
      isExternalLink: false,
    },
    {
      name: (
        <>
          よくある
          <br />
          質問
        </>
      ),
      icon: 'help_outline',
      link: 'https://support.minkabu.jp/hc/ja/categories/4405421753369',
      isExternalLink: true,
    },
  ];

  const goLink = (isExternalLink: boolean, link: string): void => {
    if (!isTransition) return;
    if (isExternalLink) {
      window.open(link, '_blank');
    } else {
      pushRouting({ path: `/${link}` }, true);
    }
  };

  return (
    <div className="pt-8">
      <div className="px-4 mb-1 text-lg font-bold">サービス一覧</div>
      <div
        className="grid ml-2 mr-4"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(85px, 1fr))' }}>
        {srviceList.map((list, index) => {
          return (
            <div
              className="flex flex-col ml-2 px-2 mt-2 border text-center border-bluegray-200 shadow-lg rounded-lg cursor-pointer"
              style={{ height: '80px', width: '80px' }}
              onClick={(): void => goLink(list.isExternalLink, list.link)}
              key={index}>
              <div
                className={`icon-${list.icon} text-xl text-red-600 flex items-end justify-center`}
                style={{ height: '42%' }}></div>
              <div
                className="flex items-center justify-center text-xs leading-4"
                style={{ height: '58%' }}>
                {list.name}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const DividendItem = ({
  isTransition = true,
}: {
  isTransition?: boolean;
}) => {
  const routing = useRouting();
  const goDividend = (): void => {
    if (!isTransition) return;
    routing.pushRouting(
      {
        path: `/dividenddeposit?utm_source=tw_minkabu&utm_medium=home&utm_campaign=bn_haitou_202507`,
      },
      true
    );
  };

  return (
    <div className="pt-8 text-lg font-bold">
      <div className="px-4 ">配当金を管理する</div>
      <div className="flex justify-center mt-2 px-4">
        <div
          className={`${isTransition ? 'cursor-pointer' : ''}`}
          onClick={(): void => goDividend()}>
          <img src="/static/images/home/dividend.svg" alt="new" className="" />
        </div>
      </div>
    </div>
  );
};

const TargetListItem = ({
  goal,
  goTargetPushDetail,
}: {
  goal: PeriodGoalItemProps;
  goTargetPushDetail: (id: string) => void;
}): React.ReactElement => {
  const refType = goal.refType as TargetType;
  const achievementAmount =
    goal.achievementAmount !== null ? goal.achievementAmount : 0;
  const targetAmount = goal.targetAmount !== null ? goal.targetAmount : 0;

  const num_ = (achievementAmount / targetAmount) * 100;
  const num = Number.isNaN(num_) ? 0 : num_;
  const percentage = num < 0 ? 0 : num > 100 ? 100 : Math.floor(num);
  const isAchieved = percentage >= 100;

  return (
    <div
      className={`px-3 py-2 mt-2 bg-white rounded-lg border shadow cursor-pointer`}
      style={{
        borderColor: '#DEE5EE',
      }}
      onClick={() => goTargetPushDetail(goal.id)}>
      <div
        className="grid items-center"
        style={{ gridTemplateColumns: 'auto 1fr auto' }}>
        <div style={{ width: '45px', height: '45px' }}>
          <TargetItemIcon {...{ targetType: refType }} />
        </div>
        <div className="flex flex-col ml-2">
          <div className="flex flex-row w-full text-sm font-bold">
            <div>{goal.title}</div>
            <div className="ml-auto">
              {isAchieved ? (
                <span className="text-orange-asset">達成</span>
              ) : (
                <>
                  {percentage}
                  <span className="text-xs">%</span>
                </>
              )}
            </div>
          </div>
          <div className="w-full mt-2">
            <div className="bg-gray-300 rounded-full overflow-hidden">
              {isAchieved ? (
                <div
                  className={'h-2'}
                  style={{
                    width: '100%',
                    backgroundImage:
                      'linear-gradient(90deg, #FFCB49 24.03%, #F97316 105.15%, #E2E8F0 105.16%)',
                  }}></div>
              ) : (
                <div
                  className="bg-teal-500 h-2"
                  style={{
                    width: `${percentage}%`,
                  }}></div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center ml-4 w-2">
          <img
            className="w-full"
            src="/static/images/icon/icon-arrow-right.svg"
            alt=""
          />
        </div>
      </div>
    </div>
  );
};

const TargetList = ({
  goals,
}: {
  goals: PeriodGoalItemProps[];
}): React.ReactElement => {
  const { goTargetIndex, goTargetPushDetail } = useTargetSupportRouting();

  return (
    <>
      <div className="pt-8 px-4">
        <div className="mb-1 text-lg font-bold">資産目標 今月の達成状況</div>
        {isPremium() ? (
          <>
            {goals.length !== 0 ? (
              <>
                {goals.map((goal, key) => {
                  return (
                    <TargetListItem {...{ goal, key, goTargetPushDetail }} />
                  );
                })}
              </>
            ) : (
              <div
                className={`px-4 py-3 mt-2 bg-white rounded-lg border shadow`}
                style={{
                  borderColor: '#DEE5EE',
                }}>
                <div>
                  <img
                    className="w-full"
                    src="/static/images/premium/premium2.png"
                    alt=""
                  />
                </div>
                <div className="mt-2 text-lg font-bold">
                  資産目標サポートを使ってみよう！
                </div>
                <div className="mt-1 text-sm">
                  これからのライフイベントに備えて、必要なお金を準備しましょう。資産目標と口座を登録すると自動で進捗が反映されます。
                </div>
                <div className="mt-3 mb-2" onClick={() => goTargetIndex()}>
                  <button className="font-bold text-base shadow text-white bg-tradewell rounded-lg p-2 w-full">
                    <div>資産目標サポートを試してみる</div>
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div onClick={() => goTargetIndex()}>
            {/* PC */}
            <img
              className="hidden lg:flex w-1/2 mx-auto cursor-pointer"
              src="/static/images/premium/try_premium1.png"
              alt=""
            />
            {/* SP */}
            <img
              className="flex lg:hidden w-full max-w-88 mx-auto cursor-pointer"
              src="/static/images/premium/try_premium1.png"
              alt=""
            />
          </div>
        )}
      </div>
    </>
  );
};

export const ColumnList = () => {
  const columList = [
    {
      title: '貯金ができる保険',
      text: '健康祝い金がもらえる保険のメリット・デメリット',
      img: 'column1.svg',
      link:
        'https://ins.minkabu.jp/columns/what-health-celebration-231215?utm_source=tw_minkabu&utm_medium=popup&utm_campaign=what-health-celebration-240618',
    },
    {
      title: '円安時のドル建て保険',
      text: 'ドル建て保険はやめたほうがいいと言われる理由は？',
      img: 'column2.svg',
      link:
        'https://ins.minkabu.jp/columns/avoid-dollar-insurance-240520?utm_source=tw_minkabu&utm_medium=popup&utm_campaign=void-dollar-insurance-240618',
    },
    {
      title: '資産運用ができる保険',
      text: '変額保険を活用した資産運用のメリットとデメリット',
      img: 'column3.svg',
      link:
        'https://ins.minkabu.jp/columns/assetmanagement-life-insurance-240327?utm_source=tw_minkabu&utm_medium=popup&utm_campaign=assetmanagement-life-insurance-240618',
    },
  ];
  return (
    <div className="pt-8 px-4">
      <div className="text-lg font-bold">お役立ちコラム</div>
      {columList.map((list, index) => {
        return (
          <div
            className="flex flex-row mt-4 items-center justify-between"
            key={index}>
            <div>
              <div className="flex flex-row text-orange-500">
                <div className="icon-check_circle text-lg"></div>
                <div className="ml-1 text-sm">{list.title}</div>
              </div>
              <div className="mt-2 text-base font-bold">
                <a href={list.link} target="_blank" rel="noopener noreferrer">
                  {list.text}
                </a>
              </div>
            </div>
            <div>
              <img
                className=""
                src={`/static/images/home/${list.img}`}
                alt={`お役立ちコラム${index}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export type PeriodGoalItemProps = {
  id: string;
  refType: number;
  title: string;
  totalTargetAmount: number;
  targetAmount: number | null;
  achievementAmount: number | null;
  completed: boolean;
};

export const TotalAssetStuffingFluctuationList = (): React.ReactElement => {
  const { data: periodGoalsData } = useQuery(
    'assetStuffingPeriodGoals',
    getPeriodGoalAPI,
    {
      refetchOnWindowFocus: false,
    }
  );

  return (
    <>
      <AssetsList />
      <ServiceList />
      <DividendItem />
      {!isIosAppNotPremium() && (
        <>
          {periodGoalsData === null || periodGoalsData === undefined ? (
            <div className="pt-8 px-4">
              <div className="text-lg font-bold">資産目標 今月の達成状況</div>
              <Loading
                loading={true}
                circularLoading={true}
                style={{
                  paddingTop: '60px',
                  paddingBottom: '60px',
                }}
              />
            </div>
          ) : (
            <TargetList
              {...{ goals: periodGoalsData as PeriodGoalItemProps[] }}
            />
          )}
        </>
      )}
      <ColumnList />
    </>
  );
};

const getPeriodGoalAPI = async () => {
  const res_ = await getPeriodGoal();
  const res = res_.Items.map(item => {
    return {
      id: item.ID,
      refType: item.RefType,
      title: item.Title,
      totalTargetAmount: item.TotalTargetAmount,
      targetAmount: item.TargetAmount,
      achievementAmount: item.AchievementAmount,
      completed: item.Completed,
    };
  });
  return res;
};
